import { IHierarchyColumn } from 'core-components/library/list/types'

const getName = (columns: IHierarchyColumn[], prefix: string, column: IHierarchyColumn): string => {
  if (column.parentField) {
    const parent = columns.find((parent) => parent.field === column.parentField)
    if (parent) {
      return getName(columns, `${parent.field}.${prefix}`, parent)
    }
    return prefix
  }
  return prefix
}

const calcVisibleHierarchyColumns = (columns: IHierarchyColumn[], keyField: string) => {
  const data = [...columns]
  return data
    .filter((column) => !column.hidden)
    .map((el) => {
      if (el.parentField) {
        const parent = data.find((parent) => parent.field === el.parentField)
        if (parent) {
          if (!parent.children) {
            parent.children = []
          }
          const isChildExist = parent.children.some((child) => child[keyField] === el[keyField])
          if (!isChildExist) {
            parent.children.push(el)
          }
        }
      }

      el.hierarchyName = getName(data, el.field, el)
      return el
    })
    .filter((el) => !el.children?.length)
}

const checkParentProperty = (columns: IHierarchyColumn[]) => {
  columns.forEach((el) => {
    if (el.parentField) {
      const parent = columns.find((parent) => parent.field === el.parentField)
      if (!parent) {
        console.error(`CORE-COMPONENTS/TABLE: Parent column with field ${el.parentField} not found`)
      }
    }
  })
}

const sortColumnsToRender = <T>(columns: IHierarchyColumn<T>[]): IHierarchyColumn<T>[] => {
  const map = new Map<string | null, IHierarchyColumn[]>()

  checkParentProperty(columns)

  columns.forEach((column) => {
    const parent = column.parentField || null
    if (!map.has(parent)) {
      map.set(parent, [])
    }
    map.get(parent)?.push(column)
  })

  function sortAndBuild(parent: string | null = null): IHierarchyColumn<T>[] {
    const children = map.get(parent) || []

    if (!parent && !children.length && children.length) {
      console.error(
        `CORE-COMPONENTS/TABLE: Can't find root columns. Check your parentField config in columns property`
      )
    }

    children.sort((a, b) => {
      const priorityA = a.priority ?? 0
      const priorityB = b.priority ?? 0
      return priorityA - priorityB
    })

    return children.flatMap((child) => [child, ...sortAndBuild(child.field)])
  }

  return sortAndBuild()
}

export const calcHeaderColumnsToRender = <T>(
  columns: IHierarchyColumn<T>[],
  keyField: string
): IHierarchyColumn<T>[][] => {
  const visibleColumns = calcVisibleHierarchyColumns(sortColumnsToRender(columns), keyField)

  const levels = visibleColumns.reduce((maxLevel, column) => {
    const level = column.hierarchyName?.split('.').length || 1
    return Math.max(maxLevel, level)
  }, 1)

  if (levels === 1) {
    return [visibleColumns]
  }

  const newColumns: IHierarchyColumn<T>[][] = []
  for (let i = 0; i < levels; i++) {
    const level: IHierarchyColumn<T>[] = []
    for (const column of visibleColumns) {
      const fieldData = column.hierarchyName?.split('.') || [column.field]
      const prevLevel = newColumns[i - 1]
      let parent
      let j = i
      while ((parent = prevLevel?.find((el) => el.field === fieldData[j - 1]))) {
        parent.colspan = (parent.colspan || 0) + 1
        j--
      }

      if (fieldData[i]) {
        const existColumn = level.find(
          (el) => el.field === fieldData[i] && el.parentField === fieldData[i - 1]
        )
        if (!existColumn) {
          const configColumn = columns.find(
            (el) => el.field === fieldData[i] && el.parentField === fieldData[i - 1]
          )
          if (configColumn) {
            level.push({
              ...configColumn,
              hierarchyName: column.hierarchyName,
            })
          }
        }
      } else {
        j = i
        while (j > 0) {
          const prevLevel = newColumns[j - 1]
          parent = prevLevel?.find((el) => el.field === fieldData[j - 1])
          if (parent) {
            parent.rowSpan = (parent.rowSpan || 1) + 1
            break
          }
          j--
        }
      }
    }

    newColumns.push(level)
  }

  return newColumns
}

export const calcColumnsToRender = <T>(
  columns: IHierarchyColumn<T>[],
  keyField: string
): IHierarchyColumn<T>[] => {
  return calcVisibleHierarchyColumns(sortColumnsToRender(columns), keyField)
}
